import React, { useState } from 'react';
import { Button } from './Button';

interface SubmitSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (suggestionText: string) => void;
}

export const SubmitSuggestionModal: React.FC<SubmitSuggestionModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [suggestionText, setSuggestionText] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestionText.trim().length < 10) {
      setError('Suggestion must be at least 10 characters long.');
      return;
    }
    setError(null);
    onSubmit(suggestionText);
    setSuggestionText(''); 
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSuggestionText(e.target.value);
    if (error && e.target.value.trim().length >= 10) {
      setError(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[100]"
      aria-labelledby="submit-suggestion-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="submit-suggestion-modal-title" className="text-xl font-semibold text-slate-800">
            Submit a Suggestion
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <p className="text-sm text-slate-600 mb-3">
            Have an idea to improve the Command Center? We'd love to hear it!
          </p>
          <textarea
            value={suggestionText}
            onChange={handleTextChange}
            placeholder="Type your suggestion here (min. 10 characters)..."
            rows={5}
            className={`w-full p-3 border ${error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#003794]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm`}
            aria-label="Suggestion text area"
            aria-describedby={error ? "suggestion-error" : undefined}
            required
            minLength={10}
          />
          {error && <p id="suggestion-error" className="text-red-500 text-xs mt-1">{error}</p>}

          <div className="border-t border-slate-200 pt-4 mt-4 flex justify-end space-x-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Submit Suggestion
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};